"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover";
import { cn, getCityNameForSearch } from "@/lib/utils";
import api from "@/client/api/axios";

const DEBOUNCE_MS = 200;

interface CityPrediction {
  id: number;
  name: string;
}

export interface LocationSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  triggerClassName?: string;
  contentClassName?: string;
}

export function LocationSelect({
  value,
  onValueChange,
  placeholder = "Search city",
  triggerClassName,
  contentClassName,
}: LocationSelectProps) {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState<CityPrediction[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const fetchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const query = inputValue.trim();
    if (!query) {
      setPredictions([]);
      setPopoverOpen(false);
      return;
    }
    if (fetchRef.current) clearTimeout(fetchRef.current);
    fetchRef.current = setTimeout(async () => {
      fetchRef.current = null;
      try {
        const response = await api.get<{
          success: boolean;
          data: CityPrediction[];
        }>("/cities", {
          params: { name: query, status: "active" },
        });
        if (response.data.success) {
          setPredictions(response.data.data || []);
          setPopoverOpen((response.data.data || []).length > 0);
        }
      } catch (error) {
        setPredictions([]);
        setPopoverOpen(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (fetchRef.current) clearTimeout(fetchRef.current);
    };
  }, [inputValue]);

  const handleSelect = useCallback(
    (city: CityPrediction) => {
      const cityName = getCityNameForSearch(city.name);
      if (cityName) {
        onValueChange(cityName);
        setInputValue(cityName);
      }
      setPopoverOpen(false);
      setPredictions([]);
    },
    [onValueChange]
  );

  const showPopover = popoverOpen && predictions.length > 0;

  return (
    <Popover open={showPopover} onOpenChange={setPopoverOpen}>
      <PopoverAnchor asChild>
        <div className="relative w-full">
          <span
            className="absolute left-4 top-1/2 -translate-y-1/2 text-primary pointer-events-none z-10"
            aria-hidden
          >
            <MapPin className="h-4 w-4 md:h-5 md:w-5 shrink-0" />
          </span>
          <Input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => predictions.length > 0 && setPopoverOpen(true)}
            placeholder={placeholder}
            className={cn(
              "w-full h-12 bg-white text-foreground font-semibold shadow-lg rounded-lg border-0 py-3 pl-10 pr-9 text-sm md:text-base focus-visible:ring-2 focus-visible:ring-primary placeholder:text-foreground/50",
              triggerClassName
            )}
            autoComplete="off"
            spellCheck={false}
          />
          {inputValue.trim() && (
            <button
              type="button"
              onClick={() => {
                setInputValue("");
                setPredictions([]);
                setPopoverOpen(false);
                onValueChange("");
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              aria-label="Clear location"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        className={cn("w-[var(--radix-popover-trigger-width)] max-h-[280px] overflow-auto p-0", contentClassName)}
        align="start"
        sideOffset={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ul className="p-1">
          {predictions.map((p, index) => (
            <li key={`${p.id}-${index}`}>
              <button
                type="button"
                className="relative flex w-full cursor-pointer items-center gap-2 rounded-sm px-3 py-2.5 text-sm text-left outline-none select-none hover:bg-muted focus:bg-muted [&_svg]:pointer-events-none [&_svg]:shrink-0"
                onClick={() => handleSelect(p)}
              >
                <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="font-medium text-foreground">
                  {p.name}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}